import mqtt from 'mqtt/dist/mqtt'
import type { MqttClient, IClientOptions } from 'mqtt'
import KeyCloakService from '@/security/KeycloakService'

const getMqttUri = (): string => {
  let uri = `wss://${location.hostname}:8084/mqtt`
  if (location.protocol !== 'https:') {
    uri = `ws://${location.hostname}:8083/mqtt`
  }
  return uri
}

const buildClientId = (prefix: string): string => {
  const rand = Math.random().toString(36).slice(2, 10)
  return `${prefix}-${rand}`
}

const isDocumentVisible = (): boolean => typeof document !== 'undefined' ? !document.hidden : true

const refreshKeycloakCredentials = async (): Promise<void> => {
  const keycloak = KeyCloakService.Instance
  try {
    await keycloak.updateToken?.(5)
  } catch (_e) {
    // ignore; will attempt with current token later
  }
}

export type CreateMqttClientOptions = {
  clientIdPrefix?: string
  protocolVersion?: 3 | 4 | 5
  clean?: boolean
  reconnectPeriodMs?: number
}

export const createMqttClient = async (opts: CreateMqttClientOptions = {}): Promise<MqttClient> => {
  const keycloak = KeyCloakService.Instance
  if (isDocumentVisible()) {
    try {
      await keycloak.updateToken?.(5)
    } catch (_e) {
      // ignore; will attempt with current token
    }
  }

  const clientOptions: IClientOptions = {
    protocolVersion: opts.protocolVersion ?? 5,
    clean: opts.clean ?? true,
    reconnectPeriod: opts.reconnectPeriodMs ?? 1000,
    clientId: buildClientId(opts.clientIdPrefix || 'console-app'),
    username: keycloak?.idTokenParsed?.sub || 'console-app',
    password: keycloak?.token,
  }

  const client = mqtt.connect(getMqttUri(), clientOptions)
  const originalReconnectPeriod = (client.options as IClientOptions).reconnectPeriod as number

  const updateClientCredentialsFromKeycloak = async () => {
    await refreshKeycloakCredentials()
    ;(client.options as IClientOptions).password = keycloak?.token
    ;(client.options as IClientOptions).username = keycloak?.idTokenParsed?.sub || 'console-app'
  }

  // Before each reconnect attempt, refresh the token and update credentials
  client.on('reconnect', async () => {
    if (!isDocumentVisible()) {
      return
    }
    try {
      await updateClientCredentialsFromKeycloak()
    } catch (_e) {
      // keep trying with existing token; next cycle will retry
    }
  })

  // If we get a not authorized error, force a quick refresh and reconnect
  client.on('error', async (err: unknown) => {
    if (!isDocumentVisible()) {
      return
    }
    const message = (err as Error)?.message?.toLowerCase() || ''
    if (message.includes('authorized') || message.includes('password')) {
      try {
        await updateClientCredentialsFromKeycloak()
        client.reconnect()
      } catch (_e) {
        // swallow
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('MQTT error', err);
    }
  })

  // Handle page visibility changes: pause reconnects when hidden, resume when visible
  if (typeof document !== 'undefined') {
    const onVisibilityChange = async () => {
      if (document.hidden) {
        (client.options as IClientOptions).reconnectPeriod = 0
      } else {
        (client.options as IClientOptions).reconnectPeriod = originalReconnectPeriod
        try {
          await updateClientCredentialsFromKeycloak()
          if (!client.connected) {
            client.reconnect()
          }
        } catch (_e) {
          // ignore
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    // Clean up the listener when the client ends
    client.on('end', () => {
      try { document.removeEventListener('visibilitychange', onVisibilityChange) } catch (_e) { /* noop */ }
    })
  }

  // If created while hidden, pause reconnect attempts until visible
  if (!isDocumentVisible()) {
    (client.options as IClientOptions).reconnectPeriod = 0
  }

  return client
}

export default {
  createMqttClient,
  getMqttUri,
}


